import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../Utils/toastContext";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";

const CustomerPopup = ({
  investmentID,
  customerId,
  onClose,
  functionName,
  functionId,
  updateTransactionHistory,
}) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const [quantities, setQuantities] = useState([]);

  const [popupData, setPopupData] = useState([]);
  const [unallocatedStockData, setUnallocatedStockData] = useState([]);

  const [popupDataTable, setPopupDataTable] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userAddedAmount, setUserAddedAmount] = useState(0);
  const inputRef = useRef(null);
  const [isNewAmount, setIsNewAmount] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [cashAccountId, setcashAccountId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stockCode, setStockCode] = useState("");
  const [stockName, setStockName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [rowData, setRowData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedStocks, setSelectedStocks] = useState([]);
  const handleSelect = (stockId) => {
    setSelectedStocks((prev) =>
      prev.includes(stockId)
        ? prev.filter((id) => id !== stockId)
        : [...prev, stockId]
    );
  };
  // Handle row selection
  const getSelectedData = () => {
    return functionId == 2
      ? selectedStocks.map((id) => ({
          stock_id: id,
          quantity: quantities[id]
            ? quantities[id].toString().replace(/,/g, "")
            : "0", // remove commas before saving
        }))
      : unallocatedStockData.map((data) => ({
          stock_id: data.stock_id,
          quantity: data.quantity, // remove commas before saving
        }));
  };
  // Handle quantity change
  const handleQuantityChange = (stockId, value) => {
    const numberValue = value.replace(/,/g, ""); // remove commas for saving
    if (!isNaN(numberValue) && Number(numberValue) >= 0) {
      setQuantities((prev) => ({
        ...prev,
        [stockId]: Number(numberValue),
      }));
    }
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem("jwtToken");
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      console.error("API Call Error:", error); // Log full error for debugging
      throw new Error(
        typeof error.message === "string"
          ? error.message
          : "Network error, please try again later"
      );
    }
  };
  const formatNumber = (value) => {
    if (!value) return "";

    // Remove commas before parsing
    const number = parseFloat(value.toString().replace(/,/g, ""));

    if (isNaN(number)) return "";

    // Format with 2 decimal places and add commas
    return number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const handleChangeAmount = (e, amountValue) => {
    e.preventDefault();
    const rawValue = e.target.value.replace(/,/g, ""); // Remove commas

    if (!isNaN(rawValue) || rawValue === "") {
      const cursorPosition = e.target.selectionStart;

      setUserAddedAmount(rawValue); // Store raw value without commas
      if (Number(rawValue) > 0) {
        setIsNewAmount(true);
      } else {
        setIsNewAmount(false);
      }
      // Wait for state update before restoring cursor position
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    }
  };
  const getPopData = async () => {
    setIsLoading(true);

    try {
      const response = await GetAPIcalls(
        `/v1/user/${customerId}/my-investment/${investmentID}/allocation`
      );
      if (response.status == 200) {
        setPopupData(response.data);
        setPopupDataTable(response.data.current_target_allocations);
        setcashAccountId(response.data.cash_accounts[0].id);
      } else {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
        }
        if (response.status === 400) {
          setPopupData([]);
          setPopupDataTable([]);
          showToast(response.message, "error");
        }
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setTimeout(function () {
        setIsLoading(false);
      }, 100);
    }
  };
  const getUnallocatedStocks = async () => {
    setIsLoading(true);

    try {
      const response = await GetAPIcalls(
        `/v1/external-stocks/unallocated-holdings/${customerId}`
      );
      if (response.status == 200) {
        setUnallocatedStockData(response.data.holdings);
      } else {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
        }
        if (response.status === 400) {
          setPopupData([]);
          setPopupDataTable([]);
          showToast(response.message, "error");
        }
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setTimeout(function () {
        setIsLoading(false);
      }, 100);
    }
  };
  const createInvestment = async () => {
    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl =
      functionId == 1
        ? `${BASE_URL}/v1/investment-request/create`
        : `${BASE_URL}/v1/user/${customerId}/my-investment/withdrawal-request`;
    const bodyData = {
      user_id: customerId,
      cash_account_id: cashAccountId,
      amount: Number(userAddedAmount),
      investment_portfolio_id: investmentID,
    };
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };
  const handleCreateInvestment = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await createInvestment();
        if (response.status == 200) {
          setIsNewAmount(false);
          toastMessage = {
            message:
              functionId == 1
                ? "Add Amount request created successfully"
                : "Withdraw Amount request created successfully",
            type: "success",
          };
          updateTransactionHistory();
          onClose("");
        }
      } catch (err) {
        toastMessage = {
          message: err.message,
          type: "error",
        };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [customerId, cashAccountId, userAddedAmount, investmentID, functionId] // Dependencies
  );
  const createSellPortfolio = async () => {
    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/v1/merchant/dashboard/my-investment/portfolio/sell-entire`;
    const bodyData = {
      user_id: customerId,
      cash_account_id: cashAccountId,
      investment_portfolio_id: investmentID,
    };
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };
  const handleSellPortfolio = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await createSellPortfolio();
        if (response.status == 200) {
          toastMessage = {
            message: "Sell Entire Portfolio Request Created Successfully",
            type: "success",
          };
          onClose("");
        }
      } catch (err) {
        toastMessage = {
          message: err.message,
          type: "error",
        };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [customerId, cashAccountId, investmentID, functionId] // Dependencies
  );
  const createWithdrawSelf = async () => {
    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/v1/investment-request/bulk-unallocated-sell`;
    const bodyData = {
      user_id: customerId,
      items: getSelectedData(),
    };
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };
  const handleWithdrawSelf = useCallback(
    debounce(async () => {
      let toastMessage = null;
      try {
        const response = await createWithdrawSelf();
        if (response.status == 201) {
          toastMessage = {
            message: "Request Created Successfully",
            type: "success",
          };
          onClose("");
        }
      } catch (err) {
        toastMessage = {
          message: err.message,
          type: "error",
        };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [customerId, selectedStocks, quantities, functionId, unallocatedStockData] // Dependencies
  );
  useEffect(() => {
    if (investmentID != -1 && Number(userAddedAmount) == 0) {
      getPopData();
    }
  }, [userAddedAmount, investmentID]);
  useEffect(() => {
    if (investmentID == -1) {
      getUnallocatedStocks();
    }
  }, [investmentID]);
  useEffect(() => {
    if (unallocatedStockData.length > 0) {
      setRowData(
        unallocatedStockData.map((row) => ({
          ...row,
          quantityFormatted: row.quantity
            ? row.quantity.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "",
        }))
      );
    }
  }, [unallocatedStockData]);

  const AddFundViewBreakdown = async () => {
    const token = localStorage.getItem("jwtToken");
    let apiUrl = "";
    if (functionId == 1) {
      apiUrl = `${BASE_URL}/v1/user/${customerId}/my-investment/add-funds`;
    }
    if (functionId == 2) {
      apiUrl = `${BASE_URL}/v1/user/${customerId}/my-investment/withdraw-funds`;
    }
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const bodyData = {
      portfolio_id: investmentID,
      amount: Number(userAddedAmount),
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(bodyData),
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };

  const handleSubmit = useCallback(
    debounce(async (e) => {
      let toastMessage = null;
      try {
        const response = await AddFundViewBreakdown();
        if (response.status === 200) {
          setPopupDataTable(response.data.current_target_allocations);
        }
      } catch (error) {
        toastMessage = { message: error.message, type: "error" };
      } finally {
        setIsLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [userAddedAmount, isNewAmount, customerId] // Dependencies
  );

  return (
    <div className="fixed inset-0 flex py-2 items-center justify-center bg-black bg-opacity-50 z-50">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div className="bg-white max-h-[90vh]  overflow-y-auto rounded-md shadow-lg p-6 w-[90%] max-w-4xl relative">
        {/* Popup Header */}
        <button
          onClick={onClose}
          className="absolute top-4 rounded-full right-4 font-medium px-2 py-1"
        >
          <img src="/add.svg" alt="Close" className="w-10 h-10" />
        </button>
        <div className="text-center mb-2">
          <h2 className="text-md font-semibold text-center">{functionName}</h2>
        </div>
        {/* Main Grid with Left and Right Sections */}
        <div className="grid grid-cols-1 gap-4">
          {/* Left Section - Document Information */}
          {functionId == 1 && (
            <div>
              <form
                className="w-full max-w-sm"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsNewAmount(false);
                  console.log("onclick called");
                  setIsLoading(true);
                  handleSubmit(e, functionId);
                }}
              >
                <label className="block text-sm text-labelLightColour font-medium">
                  Enter the amount you want to add to your portfolio
                </label>
                <input
                  type="text"
                  name="field2"
                  value={
                    isFocused ? userAddedAmount : formatNumber(userAddedAmount)
                  }
                  ref={inputRef}
                  onChange={(e) => {
                    // Avoid triggering onChange if the focus is on the button
                    if (!isFocused) return;

                    handleChangeAmount(e, e.target.value);
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={(e) => {
                    setIsFocused(false);
                  }}
                  className="block mt-2 p-2 w-40 text-black font-medium border border-gray-300 rounded-md text-md"
                />
                <div className="flex text-sm space-x-4 mt-2">
                  <button
                    type="submit"
                    disabled={isNewAmount ? false : true}
                    className={`px-8 py-2 rounded-lg cursor-pointer
           ${isNewAmount ? "bg-black text-white" : "bg-gray-200 text-black"}
           `}
                  >
                    View Breakdown
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (userAddedAmount <= 0) {
                        showToast("Please enter a valid amount", "error"); // Success toast
                        return;
                      }
                      setLoading(true);
                      handleCreateInvestment(e);
                    }}
                    className="px-8 py-2 rounded-lg bg-black text-white hover:bg-gray-800"
                  >
                    Confirm
                  </button>
                </div>
              </form>
            </div>
          )}
          {functionId == 2 && investmentID != -1 && (
            <div>
              <form
                className="w-full max-w-md"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsNewAmount(false);
                  console.log("onclick called");
                  setIsLoading(true);
                  handleSubmit(e);
                }}
              >
                <label className="block text-sm text-labelLightColour font-medium">
                  Enter the amount you want to withdraw to your portfolio
                </label>
                <input
                  type="text"
                  name="field2"
                  value={
                    isFocused ? userAddedAmount : formatNumber(userAddedAmount)
                  }
                  ref={inputRef}
                  onChange={(e) => {
                    // Avoid triggering onChange if the focus is on the button
                    if (!isFocused) return;

                    handleChangeAmount(e, e.target.value);
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={(e) => {
                    setIsFocused(false);
                  }}
                  className="block mt-2 p-2 w-40 text-black font-medium border border-gray-300 rounded-md text-md"
                />
                <div className="flex text-sm space-x-4 mt-2">
                  <button
                    type="submit"
                    disabled={isNewAmount ? false : true}
                    className={`px-8 py-2 rounded-lg cursor-pointer
           ${isNewAmount ? "bg-black text-white" : "bg-gray-200 text-black"}
           `}
                  >
                    View Breakdown
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (userAddedAmount <= 0) {
                        showToast("Please enter a valid amount", "error"); // Success toast
                        return;
                      }
                      setLoading(true);
                      handleCreateInvestment(e);
                    }}
                    className="px-8 py-2 rounded-lg bg-black text-white hover:bg-gray-800"
                  >
                    Confirm
                  </button>
                </div>
              </form>
            </div>
          )}
          {functionId == 2 && investmentID == -1 && (
            <div>
              <div className="w-full border bg-white p-2 rounded-xl shadow-md">
                <label className="block text-sm text-labelColour font-medium mb-2">
                  Select one or more stocks, then enter the quantity you wish to
                  withdraw from your portfolio.{" "}
                </label>
                {/* Table for Stock Input */}
                <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                  <table className=" min-w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="w-12 text-center px-2 py-2 border-b">
                          <input
                            type="checkbox"
                            className="custom-checkbox h-3 w-3 mr-2"
                            onChange={(e) =>
                              setSelectedStocks(
                                e.target.checked
                                  ? unallocatedStockData.map(
                                      (row) => row.stock_id
                                    )
                                  : []
                              )
                            }
                            checked={
                              selectedStocks.length ===
                                unallocatedStockData.length &&
                              unallocatedStockData.length > 0
                            }
                          />
                        </th>
                        <th className="w-28 px-3 py-2 text-left text-sm font-medium border-b">
                          Stock Code
                        </th>
                        <th className="px-3 py-2 text-left text-sm font-medium border-b">
                          Stock Name
                        </th>
                        <th className="w-32 px-3 py-2 text-left text-sm font-medium border-b">
                          Quantity
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {unallocatedStockData.length > 0 ? (
                        unallocatedStockData.map((row) => (
                          <tr
                            key={row.stock_id}
                            className="hover:bg-gray-50 text-sm font-medium"
                          >
                            <td className="w-12 text-center border-b">
                              <input
                                type="checkbox"
                                checked={selectedStocks.includes(row.stock_id)}
                                onChange={() => handleSelect(row.stock_id)}
                                className="custom-checkbox h-3 w-3 mr-2"
                              />
                            </td>
                            <td className="w-28 px-3 py-2 border-b">
                              <input
                                type="text"
                                disabled
                                value={row.stock_asx}
                                className="w-full px-2 py-1 border rounded-md bg-gray-50"
                              />
                            </td>
                            <td className="px-3 py-2 border-b">
                              <input
                                type="text"
                                disabled
                                value={row.stock_name}
                                className="w-full px-2 py-1 border rounded-md bg-gray-50"
                              />
                            </td>
                            <td className="w-32 px-3 py-2 border-b">
                              <input
                                type="text"
                                placeholder="0"
                                value={quantities[row.stock_id] || ""}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    row.stock_id,
                                    e.target.value
                                  )
                                }
                                disabled={
                                  !selectedStocks.includes(row.stock_id)
                                }
                                className="w-full px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            No results found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex justify-end space-x-3 text-sm mt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    const invalid = selectedStocks.filter(
                      (id) => !quantities[id] || quantities[id] <= 0
                    );
                    if (invalid.length > 0) {
                      showToast(
                        "Please enter a valid quantity for all selected stocks.",
                        "error"
                      );
                      return;
                    }
                    setLoading(true);
                    handleWithdrawSelf();
                  }}
                  type="submit"
                  disabled={
                    getSelectedData().length == 0 && quantities.length == 0
                  }
                  className="px-6 py-2 rounded-lg bg-black text-white hover:bg-gray-800 transition"
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
          {functionId == 3 && investmentID != -1 && (
            <div>
              <form className="w-full max-w-md">
                <div className="flex text-sm space-x-4 mt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setLoading(true);
                      handleSellPortfolio(e);
                    }}
                    className="px-8 py-2 rounded-lg bg-black text-white hover:bg-gray-800"
                  >
                    Confirm
                  </button>
                </div>
              </form>
            </div>
          )}
          {functionId == 3 && investmentID == -1 && (
            <div>
              <div className="w-full border bg-white p-2 rounded-xl shadow-md">
                {/* Table for Stock Input */}
                <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                  <table className=" min-w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="w-28 px-3 py-2 text-left text-sm font-medium border-b">
                          Stock Code
                        </th>
                        <th className="px-3 py-2 text-left text-sm font-medium border-b">
                          Stock Name
                        </th>
                        <th className="w-32 px-3 py-2 text-left text-sm font-medium border-b">
                          Quantity
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {unallocatedStockData.length > 0 ? (
                        unallocatedStockData.map((row) => (
                          <tr
                            key={row.stock_id}
                            className="hover:bg-gray-50 text-sm font-medium"
                          >
                            <td className="w-28 px-3 py-2 border-b">
                              <input
                                type="text"
                                disabled
                                value={row.stock_asx}
                                className="w-full px-2 py-1 border rounded-md bg-gray-50"
                              />
                            </td>
                            <td className="px-3 py-2 border-b">
                              <input
                                type="text"
                                disabled
                                value={row.stock_name}
                                className="w-full px-2 py-1 border rounded-md bg-gray-50"
                              />
                            </td>
                            <td className="w-32 px-3 py-2 border-b">
                              <input
                                type="text"
                                disabled
                                value={row.quantity}
                                className="w-full px-2 py-1 border rounded-md bg-gray-50"
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            No results found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex justify-end space-x-3 text-sm mt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    const invalid = selectedStocks.filter(
                      (id) => !quantities[id] || quantities[id] <= 0
                    );
                    if (invalid.length > 0) {
                      showToast(
                        "Please enter a valid quantity for all selected stocks.",
                        "error"
                      );
                      return;
                    }
                    setLoading(true);
                    handleWithdrawSelf();
                  }}
                  type="submit"
                  disabled={
                    getSelectedData().length == 0 && quantities.length == 0
                  }
                  className="px-6 py-2 rounded-lg bg-black text-white hover:bg-gray-800 transition"
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
          {/* {functionId == 2 && (
            <div>
              <label className="block text-sm text-labelLightColour font-medium">
                Enter the amount you want to withdraw to your portfolio
              </label>
              <input
                type="text"
                name="field2"
                value={formatNumber(userAddedAmount)}
                ref={inputRef}
                onChange={(e) => {
                  handleChangeAmount(e);
                }}
                className="block mt-2 p-2 w-40 text-black font-medium border border-gray-300 rounded-md text-md"
              />
            </div>
          )}
          {functionId == 3 && (
            <div>
              <label className="block text-sm text-labelLightColour font-medium">
                Enter the amount
              </label>
              <input
                type="text"
                name="field2"
                value={formatNumber(userAddedAmount)}
                ref={inputRef}
                onChange={(e) => {
                  handleChangeAmount(e);
                }}
                className="block mt-2 p-2 w-40 text-black font-medium border border-gray-300 rounded-md text-md"
              />
            </div>
          )} */}
        </div>
        {popupDataTable && popupDataTable.length > 0 && (
          <div className="overflow-x-auto mt-4">
            <table className="table-auto border-collapse w-full text-sm">
              <thead>
                <tr className="bg-[#6AA7D3] text-white text-left font-semibold">
                  <th className="border border-white  px-4 py-2">No</th>
                  <th className="border border-white  px-4 py-2">
                    Strategic Asset Allocation
                  </th>
                  <th className="border border-white  px-4 py-2">SAA</th>
                  <th className="border border-white  px-4 py-2">Capital</th>
                  {popupDataTable[0].new_amount && (
                    <th className="border border-white  px-4 py-2">
                      New Capital
                    </th>
                  )}
                  {popupDataTable[0].new_amount && (
                    <th className="border border-white  px-4 py-2">
                      Difference
                    </th>
                  )}
                </tr>
              </thead>
              {isLoading ? (
                <tbody className="shimmer-container-list ">
                  {/* Creating 5 rows of shimmer effect */}
                  {[...Array(2)].map((_, rowIndex) => (
                    <tr key={rowIndex} className="shimmer-row-list">
                      {/* Creating 7 cells in each row for shimmer */}
                      {[...Array(6)].map((_, colIndex) => (
                        <td key={colIndex} className="shimmer-cell-list">
                          <div className="shimmer-loader"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  {popupDataTable.map((categoryData, categoryIndex) => (
                    <React.Fragment key={categoryIndex}>
                      {/* Category Row */}
                      <tr
                        className={`px-6 py-2 rounded-lg ${
                          categoryData.category.toUpperCase() == "GROWTH"
                            ? "bg-[#92C5EA]"
                            : "bg-[#A9D4AB]"
                        }`}
                      >
                        <td
                          colSpan={1}
                          className="border border-white px-4 py-2 font-bold"
                        ></td>
                        <td
                          colSpan={1}
                          className="border border-white px-4 py-2 font-bold"
                        >
                          {categoryData.category.toUpperCase()}
                        </td>
                        <td
                          colSpan={1}
                          className="border border-white  px-4 py-2 font-bold"
                        ></td>
                        <td
                          colSpan={1}
                          className="border border-white px-4 py-2 font-bold"
                        ></td>
                        {popupDataTable[0].new_amount && (
                          <td
                            colSpan={1}
                            className="border border-white px-4 py-2 font-bold"
                          ></td>
                        )}
                        {popupDataTable[0].new_amount && (
                          <td
                            colSpan={1}
                            className="border border-white px-4 py-2 font-bold"
                          ></td>
                        )}
                      </tr>

                      {/* Products */}
                      {categoryData.product_allocations.map(
                        (product, productIndex) => (
                          <React.Fragment key={productIndex}>
                            {/* Product Row */}
                            <tr
                              className={`text-[#005A9C] ${
                                categoryData.category.toUpperCase() == "GROWTH"
                                  ? "bg-[#C6DFF1]"
                                  : "bg-[#C0E2C1]"
                              }`}
                            >
                              <td
                                colSpan={1}
                                className="border border-white px-4 py-2 font-bold"
                              >
                                {(productIndex + 1).toString().padStart(2, "0")}
                              </td>
                              <td
                                colSpan={1}
                                className="border border-white px-4 py-2 font-semibold"
                              >
                                {product.product_name}
                              </td>
                              <td
                                colSpan={1}
                                className="border border-white px-4 py-2 font-bold"
                              >
                                {Number(product.current_allocation)
                                  .toFixed(2)
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                %
                              </td>
                              <td
                                colSpan={1}
                                className="border border-white px-4 py-2 font-bold"
                              >
                                <span>
                                  {product.amount
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",") ||
                                    "-"}
                                </span>
                              </td>
                              {product.new_amount && (
                                <td
                                  colSpan={1}
                                  className="border border-white px-4 py-2 font-bold"
                                >
                                  <span className="text-black">
                                    {" "}
                                    (
                                    {product.new_amount
                                      ? product.new_amount
                                          .toFixed(2)
                                          .toString()
                                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                      : ""}
                                    )
                                  </span>
                                </td>
                              )}
                              {product.new_amount && (
                                <td
                                  colSpan={1}
                                  className="border border-white px-4 py-2 font-bold"
                                >
                                  {product.additional_amount && (
                                    <span className="text-[#34C046]">
                                      {" +"}
                                      {product.additional_amount
                                        ? product.additional_amount
                                            .toFixed(2)
                                            .toString()
                                            .replace(
                                              /\B(?=(\d{3})+(?!\d))/g,
                                              ","
                                            )
                                        : ""}
                                    </span>
                                  )}
                                  {product.withdrawn_amount && (
                                    <span className="text-[#FF5050]">
                                      {" -"}
                                      {product.withdrawn_amount
                                        .toFixed(2)
                                        .toString()
                                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                    </span>
                                  )}
                                </td>
                              )}
                            </tr>
                          </React.Fragment>
                        )
                      )}
                      <tr
                        className={`font-bold ${
                          categoryData.category.toUpperCase() == "GROWTH"
                            ? "bg-[#92C5EA]"
                            : "bg-[#A9D4AB]"
                        }`}
                      >
                        <td colSpan={1} className="border px-4 py-2"></td>
                        <td colSpan={1} className="border px-4 py-2">
                          SUB-TOTAL {categoryData.category}
                        </td>
                        <td colSpan={1} className="border px-4 py-2">
                          {Number(categoryData.target_allocation).toFixed(2)}%
                        </td>
                        <td colSpan={1} className="border px-4 py-2">
                          <span>
                            {categoryData.amount
                              .toFixed(2)
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "-"}
                          </span>
                        </td>
                        {categoryData.new_amount && (
                          <td colSpan={1} className="border px-4 py-2">
                            <span className="text-black">
                              {" "}
                              (
                              {categoryData.new_amount
                                ? categoryData.new_amount
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : ""}
                              )
                            </span>
                          </td>
                        )}
                        {categoryData.new_amount && (
                          <td colSpan={1} className="border px-4 py-2">
                            {functionId == 1 && categoryData.new_amount && (
                              <span className="text-[#34C046]">
                                {" +"}
                                {categoryData.new_amount
                                  ? (
                                      categoryData.new_amount -
                                      categoryData.amount
                                    )
                                      .toFixed(2)
                                      .toString()
                                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                  : ""}
                              </span>
                            )}
                            {functionId == 2 && categoryData.new_amount && (
                              <span className="text-[#FF5050]">
                                {" -"}
                                {categoryData.new_amount
                                  ? (
                                      categoryData.amount -
                                      categoryData.new_amount
                                    )
                                      .toFixed(2)
                                      .toString()
                                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                  : ""}
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    </React.Fragment>
                  ))}
                  <React.Fragment>
                    <tr className="bg-[#EAEAEA] font-bold">
                      <td colSpan={1} className="border px-4 py-2 "></td>
                      <td colSpan={1} className="border px-4 py-2">
                        Summary
                      </td>
                      <td colSpan={1} className="border px-4 py-2"></td>
                      <td colSpan={1} className="border px-4 py-2"></td>
                      {popupDataTable[0].new_amount && (
                        <td colSpan={1} className="border px-4 py-2"></td>
                      )}
                      {popupDataTable[0].new_amount && (
                        <td colSpan={1} className="border px-4 py-2"></td>
                      )}
                    </tr>
                    <tr className="bg-[#A9D4AB]  font-bold">
                      <td
                        colSpan={1}
                        className="border px-4 py-2 text-right"
                      ></td>
                      <td colSpan={1} className="border px-4 py-2">
                        {popupDataTable[0].category}
                      </td>
                      <td colSpan={1} className="border px-4 py-2">
                        {Number(popupDataTable[0].target_allocation).toFixed(2)}
                        %
                      </td>
                      <td colSpan={1} className="border px-4 py-2">
                        <span>
                          {popupDataTable[0].amount
                            .toFixed(2)
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "-"}
                        </span>
                      </td>
                      {popupDataTable[0].new_amount && (
                        <td colSpan={1} className="border px-4 py-2">
                          <span className="text-black">
                            {" "}
                            (
                            {popupDataTable[0].new_amount
                              ? popupDataTable[0].new_amount
                                  .toFixed(2)
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                              : ""}
                            )
                          </span>
                        </td>
                      )}
                      {popupDataTable[0].new_amount && (
                        <td colSpan={1} className="border px-4 py-2">
                          {functionId == 1 && popupDataTable[0].new_amount && (
                            <span className="text-[#34C046]">
                              {" +"}
                              {popupDataTable[0].new_amount
                                ? (
                                    popupDataTable[0].new_amount -
                                    popupDataTable[0].amount
                                  )
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : ""}
                            </span>
                          )}
                          {functionId == 2 && popupDataTable[0].new_amount && (
                            <span className="text-[#FF5050]">
                              {" -"}
                              {popupDataTable[0].new_amount
                                ? (
                                    popupDataTable[0].amount -
                                    popupDataTable[0].new_amount
                                  )
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : ""}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                    <tr className="bg-[#92C5EA] font-bold">
                      <td colSpan={1} className="border px-4 py-2 "></td>
                      <td colSpan={1} className="border px-4 py-2">
                        {popupDataTable[1].category}
                      </td>
                      <td colSpan={1} className="border px-4 py-2">
                        {Number(popupDataTable[1].target_allocation).toFixed(2)}
                        %
                      </td>
                      <td colSpan={1} className="border px-4 py-2">
                        <span>
                          {popupDataTable[1].amount
                            .toFixed(2)
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "-"}
                        </span>
                      </td>
                      {popupDataTable[1].new_amount && (
                        <td colSpan={1} className="border px-4 py-2">
                          <span className="text-black">
                            {" "}
                            (
                            {popupDataTable[1].new_amount
                              ? popupDataTable[1].new_amount
                                  .toFixed(2)
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                              : ""}
                            )
                          </span>
                        </td>
                      )}
                      {popupDataTable[1].new_amount && (
                        <td colSpan={1} className="border px-4 py-2">
                          {functionId == 1 && popupDataTable[1].new_amount && (
                            <span className="text-[#34C046]">
                              {" +"}
                              {popupDataTable[1].new_amount
                                ? (
                                    popupDataTable[1].new_amount -
                                    popupDataTable[1].amount
                                  )
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : ""}
                            </span>
                          )}
                          {functionId == 2 && popupDataTable[1].new_amount && (
                            <span className="text-[#FF5050]">
                              {" -"}
                              {popupDataTable[1].new_amount
                                ? (
                                    popupDataTable[1].amount -
                                    popupDataTable[1].new_amount
                                  )
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : ""}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                    <tr className="bg-[#CDCDCD] font-bold">
                      <td colSpan={1} className="border px-4 py-2"></td>
                      <td colSpan={1} className="border px-4 py-2">
                        TOTAL PORTFOLIO
                      </td>
                      <td colSpan={1} className="border px-4 py-2">
                        {(
                          Number(popupDataTable[0].target_allocation) +
                          Number(popupDataTable[1].target_allocation)
                        ).toFixed(2) + "%" || "-"}
                      </td>
                      <td colSpan={1} className="border px-4 py-2">
                        <span>
                          {Number(
                            popupDataTable[0].amount + popupDataTable[1].amount
                          )
                            .toFixed(2)
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "-"}
                        </span>
                      </td>
                      {popupDataTable[1].new_amount && (
                        <td colSpan={1} className="border px-4 py-2">
                          <span className="text-black">
                            {" "}
                            (
                            {popupDataTable[0].new_amount
                              ? Number(
                                  popupDataTable[0].new_amount +
                                    popupDataTable[1].new_amount
                                )
                                  .toFixed(2)
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                              : ""}
                            )
                          </span>
                        </td>
                      )}
                      {popupDataTable[1].new_amount && (
                        <td colSpan={1} className="border px-4 py-2">
                          {functionId == 1 && popupDataTable[0].new_amount && (
                            <span className="text-[#34C046]">
                              {" +"}
                              {popupDataTable[0].new_amount
                                ? Number(
                                    popupDataTable[1].new_amount -
                                      popupDataTable[1].amount +
                                      (popupDataTable[0].new_amount -
                                        popupDataTable[0].amount)
                                  )
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : ""}
                            </span>
                          )}
                          {functionId == 2 && popupDataTable[0].new_amount && (
                            <span className="text-[#FF5050]">
                              {" -"}
                              {popupDataTable[0].new_amount
                                ? Number(
                                    popupDataTable[1].amount -
                                      popupDataTable[1].new_amount +
                                      (popupDataTable[0].amount -
                                        popupDataTable[0].new_amount)
                                  )
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : ""}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  </React.Fragment>
                </tbody>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPopup;
